import React, { useCallback, useEffect, useState } from 'react';

import { fetchUsers } from '../../api/users';
import { listMomentShopAllowlist, setSearchFeatureFlag, clearSearchFeatureFlags } from '../../api/moments';
import UserSearchPicker from '../General/UserSearchPicker';
import ShopmyImage from '../General/ShopmyImage';
import { getSmartImage } from '../../helpers/helpers';
import { shortDate } from './moment_stream_helpers';

import './MomentsFeatureFlag.scss';
import { confirmAlert } from 'react-confirm-alert';

const userInitials = user => {
  const parts = String(user.name || user.username || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '#';
  return ((parts[0][0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '#';
};

const UserAvatar = ({ user }) => {
  const imageUrl = getSmartImage(user.image);
  const [failed, setFailed] = React.useState(false);
  if (imageUrl && !failed) {
    return <ShopmyImage className='msa-feature-flag-avatar' source={imageUrl} alt='' referrerPolicy='no-referrer' onError={() => setFailed(true)} />;
  }
  return (
    <span className='msa-feature-flag-avatar is-empty' aria-hidden='true'>
      {userInitials(user)}
    </span>
  );
};

const searchAllowlistUsers = async query => {
  const data = await fetchUsers({ search: query, limit: 10 });
  return data.users || [];
};

const MomentsFeatureFlag = () => {
  const [users, setUsers] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busyUserIds, setBusyUserIds] = useState(() => new Set());

  const refresh = useCallback(() => {
    setLoadError(null);
    return listMomentShopAllowlist()
      .then(data => setUsers(Array.isArray(data.users) ? data.users : []))
      .catch(err => {
        setUsers(null);
        setLoadError(typeof err === 'string' ? err : 'Could not load the feature-flag allowlist.');
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const withBusy = async (userId, work) => {
    setBusyUserIds(prev => new Set(prev).add(userId));
    try {
      await work();
      await refresh();
    } catch (err) {
      // A failed WRITE is a toast, not the page-level outage panel — the
      // list on screen is still valid (loadError would replace it wholesale).
      window.ALERT.error(typeof err === 'string' ? err : 'Could not update the feature-flag allowlist.');
    } finally {
      setBusyUserIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  // Persist immediately: the server lists only flagged users, so a local-only addition vanishes on refresh.
  const addUsers = nextUsers => {
    const existing = new Set((users || []).map(user => user.id));
    const added = nextUsers.find(user => !existing.has(user.id));
    if (!added) return;
    withBusy(added.id, () => setSearchFeatureFlag(added.id, 'momentShops', true));
  };

  const removeUser = user => {
    // Destructive: wipes ALL of this user's pilot flags at once — same
    // confirm-first pattern (react-confirm-alert) as MomentShopPane's
    // archive/remove actions.
    confirmAlert({
      title: 'Remove search preview access?',
      message: `${user.name || user.username || `User ${user.id}`} loses search preview access. Moments remain available in the app.`,
      buttons: [
        { label: 'Cancel', className: 'cancel' },
        { label: 'Remove', onClick: () => withBusy(user.id, () => clearSearchFeatureFlags(user.id)) }
      ]
    });
  };

  return (
    <div className='msa-feature-flag'>
      <div className='msa-feature-flag-intro'>
        <p>Search preview access. All active accounts can use Moments in the app without joining this list.</p>
        <ul>
          <li>
            <strong>Listed users get search preview:</strong> conversational /search with Atelier retrieval and vibe-ranked creators. Web browsing and
            editing of Moments are limited to listed admins; shared Moment links stay viewable.
          </li>
          <li>
            <strong>Remove clears these preview flags.</strong> It does not revoke Moment access in the app.
          </li>
          <li>
            <strong>Admins don&rsquo;t need to be listed</strong> to observe Moments from this page or open a Moment link.
          </li>
          <li>
            <strong>Engine comparison switches</strong> (&ldquo;use atelier&rdquo;, &ldquo;use vibe search&rdquo;) live on /search — every listed user
            gets them, and admins see them in admin control mode. Session-only; they never change this list.
          </li>
        </ul>
        <UserSearchPicker
          selectedUsers={users || []}
          onChange={addUsers}
          searchUsers={searchAllowlistUsers}
          placeholder='Add a user by name, username, or email…'
          showUserIds
          hideSelectedList
        />
      </div>

      {users === null && !loadError && (
        <div className='msa-feature-flag-loading'>
          <div className='msa-spinner' />
          <span>Loading allowlist…</span>
        </div>
      )}

      {loadError && (
        <div className='msa-feature-flag-error'>
          <p>{loadError}</p>
          <button className='msa-primary-btn' type='button' onClick={refresh}>
            Try again
          </button>
        </div>
      )}

      {users !== null && users.length === 0 && !loadError && (
        <div className='msa-feature-flag-empty'>
          <h2>Nobody allowlisted yet</h2>
          <p>Search above to enable search preview and internal admin web previews.</p>
        </div>
      )}

      {users !== null && users.length > 0 && (
        <div className='msa-gallery-table-wrap'>
          <table className='msa-gallery-table'>
            <thead>
              <tr>
                <th>User</th>
                <th>Username</th>
                <th>Email</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td className='msa-td-title'>
                    <div className='msa-feature-flag-shopper'>
                      <UserAvatar user={user} />
                      <span>
                        {user.name || 'No name'}
                        <span className='msa-table-slug'>#{user.id}</span>
                      </span>
                    </div>
                  </td>
                  <td>{user.username ? `@${user.username}` : <span className='msa-table-empty'>—</span>}</td>
                  <td>{user.email || <span className='msa-table-empty'>—</span>}</td>
                  <td>{user.enabledAt ? shortDate(user.enabledAt) : <span className='msa-table-empty'>—</span>}</td>
                  <td>
                    <button className='msa-feature-flag-remove' type='button' disabled={busyUserIds.has(user.id)} onClick={() => removeUser(user)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MomentsFeatureFlag;
